import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { balanceService } from '../services/balanceService';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatNumber } from '../utils/formatNumber';

interface BalanceWidgetSimpleProps {
  onRefresh?: () => void;
}

const BalanceWidgetSimple: React.FC<BalanceWidgetSimpleProps> = ({ onRefresh }) => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadBalance();
  }, []);

  const loadBalance = async () => {
    try {
      setLoading(true);
      const response = await balanceService.getCurrentBalance();
      setBalance(response);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar balance');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await balanceService.recalculate();
      await loadBalance();
      enqueueSnackbar('Balance actualizado', { variant: 'success' });
      if (onRefresh) onRefresh();
    } catch (err: any) {
      enqueueSnackbar('Error al actualizar balance', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  const getBalanceColor = () => {
    if (!balance) return '#1976d2';
    const currentBalance = balance.current?.current_balance || 0;
    const minBalance = balance.account?.minimum_balance || 10000;
    
    if (currentBalance < minBalance) return '#d32f2f'; // Rojo - saldo bajo
    if (currentBalance > 100000) return '#2e7d32'; // Verde - excelente
    if (currentBalance > 50000) return '#1976d2'; // Azul - bueno
    if (currentBalance > 0) return '#ed6c02'; // Naranja - regular
    return '#d32f2f'; // Rojo - negativo
  };

  const getBalanceIcon = () => {
    if (!balance) return <AccountBalanceIcon />;
    const currentBalance = balance.current?.current_balance || 0;
    const minBalance = balance.account?.minimum_balance || 10000;
    
    if (currentBalance < minBalance) return <WarningIcon />;
    if (currentBalance > 100000) return <CheckCircleIcon />;
    return <AccountBalanceIcon />;
  };

  const getBalanceStatus = () => {
    if (!balance) return 'Cargando...';
    const currentBalance = balance.current?.current_balance || 0;
    const minBalance = balance.account?.minimum_balance || 10000;
    
    if (currentBalance < minBalance) return 'Saldo Bajo';
    if (currentBalance > 100000) return 'Excelente';
    if (currentBalance > 50000) return 'Saludable';
    if (currentBalance > 0) return 'Regular';
    return 'Crítico';
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" height={150}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !balance) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Alert severity="error" sx={{ height: '100%' }}>
            {error || 'Error al cargar balance'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const balanceColor = getBalanceColor();
  const currencySymbol = balance.account?.currency?.symbol || '₽';
  const currentBalance = balance.current?.current_balance || 0;

  return (
    <Card sx={{ height: '100%', position: 'relative' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                backgroundColor: balanceColor,
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                mr: 1
              }}
            >
              {getBalanceIcon()}
            </Box>
            <Typography variant="h6" component="div">
              Balance General
            </Typography>
          </Box>
          <Tooltip title="Actualizar">
            <IconButton size="small" onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon fontSize="small" sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="h4" component="div" sx={{ color: balanceColor, fontWeight: 'bold', mb: 1 }}>
          {formatNumber(currentBalance,2, currencySymbol)}
          <Chip
          label={getBalanceStatus()}
          size="small"
          sx={{
            backgroundColor: balanceColor,
            color: 'white',
            fontWeight: 'medium',
            mb: 2,
            fontSize:'xs'
          }}
        />
        </Typography>

        {/*<Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Ingresos Mes
            </Typography>
            <Box display="flex" alignItems="center">
              <TrendingUpIcon fontSize="small" sx={{ color: '#2e7d32', mr: 0.5 }} />
              <Typography variant="body2" fontWeight="medium" color="#2e7d32">
                {currencySymbol} {balance.current?.monthly_income?.toLocaleString() || 0}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Gastos Mes
            </Typography>
            <Box display="flex" alignItems="center">
              <TrendingDownIcon fontSize="small" sx={{ color: '#d32f2f', mr: 0.5 }} />
              <Typography variant="body2" fontWeight="medium" color="#d32f2f">
                {currencySymbol} {balance.current?.monthly_expenses?.toLocaleString() || 0}
              </Typography>
            </Box>
          </Box>
        </Box>
        */}

        <Button
          fullWidth
          size="small"
          variant="text"
          onClick={() => navigate('/balance')}
          startIcon={<HistoryIcon />}
          sx={{ mt: 2 }}
        >
          Ver detalles
        </Button>
      </CardContent>
    </Card>
  );
};

export default BalanceWidgetSimple;